// Buyer-facing fee math for the ticket checkout.
//
// This mirrors the backend remittance logic in
// app/Http/Controllers/Api/V1/PaymentController.php so the amount we charge
// through Stripe always equals the sum of `price_paid` the backend records.
//
// Two fees exist, sourced from the country's GlobalSetting row:
//   - Payment Fee (PF) = Online_Payment_Fee_percentage % of price
//                        + Online_Payment_Fee_dollar_amount
//   - Service Fee (SF) = Online_Service_Fee_percentage % of price
//                        + Online_Service_Fee_dollar_amount
//
// Each ticket carries two flags that decide who absorbs each fee:
//   - pass_payment_fee_to_buyer  → buyer pays PF (else the host does)
//   - pass_service_fee_to_buyer  → buyer pays SF (else the host does)
//
// yielding the 4 configurations (buyer side shown here):
//   1. both passed   → buyer pays base + PF + SF
//   2. PF→buyer      → buyer pays base + PF
//   3. SF→buyer      → buyer pays base + SF   (platform default)
//   4. both absorbed → buyer pays base
//
// IMPORTANT — matching the backend exactly. computeCartFees() below produces the
// amount actually charged (Payment.jsx feeds its `total` straight to the
// gateway), so it has to track PaymentController::paymentSuccess line for line.
//
// Two rules, applied in every configuration:
//   - SERVICE fee is PER TICKET:
//         line service fee = serviceFixed + servicePct% x ticket price
//     The buyer pays it only when pass_service_fee_to_buyer is set (else the host
//     absorbs it out of remittance — either way it never disappears).
//   - PAYMENT fee is ONCE PER ORDER (a per-transaction card cost, not per ticket):
//         payment fee = paymentFixed + paymentPct% x payment_fee_base
//         payment_fee_base = SUM(ticket prices) + SUM(buyer-paid service fees)
//     The base is everything the card processes EXCEPT the payment fee itself, so
//     the service fee is in the base only when the BUYER is paying it (a fee on the
//     fee). The buyer is charged this fee only when pass_payment_fee_to_buyer is
//     set (else the host absorbs it). Backend-side it lands on a single anchor row
//     (the parent) and every sibling books 0.00 in its payment columns.
//
// The four pass_* combinations ARE the four configurations, e.g. for 3x $1000 with
// SF = $210 + 2.4% and PF = $40 + 4%:
//   - BUYER_PAYS_ALL       buyer = 3000 + 702 (SF) + 188.08 (PF on 3702) = 3890.08
//   - BUYER_PAYS_PAYMENT   buyer = 3000 +   0     + 160.00 (PF on 3000) = 3160.00
//
// Consequence for the UI: the per-ticket "+ Fee" hint (buyerFeeForItem) prices ONE
// ticket all-in — its flat PF is charged once per ticket there — so the hints NO
// LONGER SUM to the cart total once quantity > 1 (the order charges the flat PF
// once). computeCartFees() is the single source of truth for the amount charged.

const isFlagOn = (v) => v === true || v === 1 || v === '1';
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Pull the fee config (rates) out of a country GlobalSetting row.
 * @param {object} row - an entry from state.event.getCountryFeeListDetails
 */
// Accepts either shape that reaches it: a raw Global_Settings country row from
// getCountryList, or the event's server-resolved `fee_settings` (which honors
// custom_pricing_id). Both now carry Online_Service_Fee_*.
//
// The buyer_ticket_fee_* fallbacks are for a response cached before those keys
// were added — the country-list endpoint caches for an hour. `??` (not `||`) so
// a genuine 0 rate is respected instead of falling through to the alias.
export const feeConfigFromCountryRow = (row) => ({
  paymentPct: num(row?.Online_Payment_Fee_percentage),
  paymentFixed: num(row?.Online_Payment_Fee_dollar_amount),
  servicePct: num(row?.Online_Service_Fee_percentage ?? row?.buyer_ticket_fee_amount_percentage),
  serviceFixed: num(row?.Online_Service_Fee_dollar_amount ?? row?.buyer_ticket_fee_dollar_amount),
});

/**
 * Per-ticket "+ Fee" hint for a single line — what ONE ticket costs all-in on top
 * of its price, honoring both flags:
 *   + service fee                    when pass_service_fee_to_buyer
 *   + payment fee (on price + that service fee) when pass_payment_fee_to_buyer
 *
 * This prices a single ticket, so its flat payment fee is included once here. The
 * order charges the flat payment fee once for the WHOLE order, so these hints no
 * longer sum to the cart total once quantity > 1 — that is expected. Never total
 * this function to price a cart; computeCartFees() is the source of truth.
 */
export const buyerFeeForItem = (item, config) => {
  const base = num(item?.fee);
  if (base <= 0) return 0;

  let fee = 0;
  let paymentBase = base;
  if (isFlagOn(item?.pass_service_fee_to_buyer)) {
    const serviceFee = num(config?.serviceFixed) + (num(config?.servicePct) / 100) * base;
    fee += serviceFee;
    paymentBase += serviceFee; // payment fee is levied on the service fee too
  }
  if (isFlagOn(item?.pass_payment_fee_to_buyer)) {
    fee += num(config?.paymentFixed) + (num(config?.paymentPct) / 100) * paymentBase;
  }
  return fee;
};

/**
 * Compute the buyer-facing breakdown for a cart. THIS is the charge amount.
 * @param {Array} items - cart lines (each with `fee` + the two pass_* flags)
 * @param {object} config - output of feeConfigFromCountryRow
 * @returns {{subtotal:number, serviceFee:number, paymentFee:number, total:number}}
 */
export const computeCartFees = (items, config) => {
  const cart = Array.isArray(items) ? items : [];
  const { paymentPct, paymentFixed, servicePct, serviceFixed } = config || {};

  let subtotal = 0;
  let serviceFee = 0; // service fees the BUYER pays (per ticket)

  // Base for the ONE order-level payment fee: every non-free ticket price plus
  // any service fee the buyer is paying (a fee on the fee). Free lines (price 0)
  // are always $0 to the buyer and never attach a fee — the flat components would
  // otherwise put a price on a "free" ticket. Mirrors the backend, which excludes
  // them from the base and from being the anchor row.
  let paymentFeeBase = 0;
  // The buyer bears the order payment fee only when the anchor line — the first
  // non-free ticket, matching the backend's anchor — passes it to the buyer.
  let anchorPaysPayment = null;

  cart.forEach((item) => {
    const base = num(item?.fee);
    subtotal += base;
    if (base <= 0) return;

    if (anchorPaysPayment === null) {
      anchorPaysPayment = isFlagOn(item?.pass_payment_fee_to_buyer);
    }

    paymentFeeBase += base;
    // Service fee is per ticket; the buyer pays it only when the flag is set.
    if (isFlagOn(item?.pass_service_fee_to_buyer)) {
      const lineService = num(serviceFixed) + (num(servicePct) / 100) * base;
      serviceFee += lineService;
      paymentFeeBase += lineService;
    }
  });

  // Payment fee: charged ONCE for the whole order. Buyer-facing, so it counts
  // only when the buyer bears it (else the host absorbs it out of remittance and
  // it is not part of the checkout total).
  let paymentFee = 0;
  if (anchorPaysPayment) {
    paymentFee = num(paymentFixed) + (num(paymentPct) / 100) * paymentFeeBase;
  }

  return {
    subtotal,
    serviceFee,
    paymentFee,
    total: subtotal + serviceFee + paymentFee,
  };
};
